'use client'

import { apiSubscribeNewsletter } from '@/apis/page_optimization'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { Send, CheckCircle, Loader2 } from 'lucide-react'
import { RecaptchaProvider } from '@/components/shared/RecaptchaProvider'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { apiVerifyCaptcha } from '@/apis/captcha'

function InnerFooterForm({ companyId, domain }: { companyId: string; domain?: string }) {
    const [email, setEmail] = useState('')
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)
    const { executeRecaptcha } = useGoogleReCaptcha()

    const validateEmail = (emailStr: string) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return regex.test(emailStr)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!email.trim()) {
            toast.error('Email field is required.', { autoClose: 2000 })
            return
        }

        if (!validateEmail(email)) {
            toast.error('Please enter a valid email address.', { autoClose: 2000 })
            return
        }

        setLoading(true)

        try {
            // Step 1: Verify token using domain if recaptcha is available
            if (executeRecaptcha && domain) {
                try {
                    const recaptchaToken = await executeRecaptcha('newsletter_form')
                    if (recaptchaToken) {
                        const captchaRes = await apiVerifyCaptcha(domain, recaptchaToken)
                        if (captchaRes && captchaRes.verified === false) {
                            toast.error('Captcha verification failed. Please try again.', { autoClose: 2000 })
                            setLoading(false)
                            return
                        }
                    }
                } catch (recaptchaErr) {
                    console.warn('reCAPTCHA skipped or failed:', recaptchaErr)
                }
            }

            // Step 2: Subscribe newsletter using companyId
            const response = await apiSubscribeNewsletter(companyId, email)
            const resMsg = typeof response?.message === 'string' ? response.message.trim() : ''
            const resStatus: any = response?.status

            const isSuccess =
                (
                    resStatus === true ||
                    resStatus === 'true' ||
                    resStatus === 200 ||
                    resStatus === '200' ||
                    resStatus === 201 ||
                    resStatus === '201' ||
                    (response as any)?.success === true ||
                    resMsg.toLowerCase().includes('subscribed successfully') ||
                    resMsg.toLowerCase().includes('subscriber successfully added') ||
                    resMsg.toLowerCase().includes('thank you') ||
                    resMsg.toLowerCase().includes('success') ||
                    resMsg.toLowerCase().includes('already') ||
                    resMsg.toLowerCase().includes('exists') ||
                    resMsg.toLowerCase().includes('taken')
                ) &&
                !resMsg.includes('Server responded with status') &&
                !resMsg.toLowerCase().includes('fail') &&
                !resMsg.toLowerCase().includes('not found')

            if (isSuccess) {
                toast.success('Thank you for subscribing!', { autoClose: 2500 })
                setEmail('')
                setSuccess(true)
                setTimeout(() => setSuccess(false), 3500)
            } else if (resMsg && resMsg !== '{}' && !resMsg.includes('Server responded with status')) {
                toast.error(resMsg, { autoClose: 2500 })
            } else {
                toast.error('Failed to subscribe. Please try again later.', { autoClose: 2500 })
            }
        } catch (error) {
            console.error('Error subscribing:', error)
            toast.error('Failed to subscribe. Please try again later.', { autoClose: 2500 })
        } finally {
            setLoading(false)
        }
    }

    return (
        <form
            className="relative mt-3 w-full max-w-full sm:max-w-[320px]"
            onSubmit={handleSubmit}
            autoComplete="off"
        >
            <div className="relative flex items-center p-1 sm:p-1.5 bg-[#071822]/90 backdrop-blur-md rounded-2xl border border-[#00d69f]/30 shadow-inner focus-within:border-[#00d69f] focus-within:ring-2 focus-within:ring-[#00d69f]/20 transition-all duration-300">
                <input
                    type="email"
                    placeholder="Enter email..."
                    className="w-full min-w-0 flex-1 bg-transparent px-2.5 py-1.5 text-white text-xs outline-none placeholder:text-white/40 font-medium"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                />
                <button
                    type="submit"
                    disabled={loading}
                    aria-label="Subscribe to newsletter"
                    className={`
                        flex items-center justify-center rounded-xl px-3 sm:px-3.5 py-1.5 sm:py-2 shrink-0
                        transition-all duration-300 active:scale-95 text-[11px] sm:text-xs font-black uppercase tracking-wider text-[#071822] shadow-md
                        ${loading
                            ? 'bg-white/10 text-white/40 cursor-not-allowed'
                            : 'bg-[#00d69f] hover:bg-[#00c28f] shadow-[#00d69f]/30 hover:shadow-[#00d69f]/50 hover:scale-[1.02]'}
                    `}
                >
                    {loading ? (
                        <Loader2 className="w-3.5 h-3.5 border-2 border-[#071822]/30 border-t-[#071822] rounded-full animate-spin" />
                    ) : success ? (
                        <CheckCircle className="w-3.5 h-3.5 text-[#071822]" />
                    ) : (
                        <div className="flex items-center gap-1.5">
                            <Send className="w-3 h-3 text-[#071822]" />
                        </div>
                    )}
                </button>
            </div>
        </form>
    )
}

const FooterNewsletter = (props: { companyId: string; domain?: string }) => {
    return (
        <RecaptchaProvider domain={props.domain || ''}>
            <InnerFooterForm {...props} />
        </RecaptchaProvider>
    )
}

export default FooterNewsletter